import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useOutletContext, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaArrowLeft, FaMapPin, FaImage, FaTrash,
  FaTimes, FaExternalLinkAlt,
  FaHardHat, FaFire, FaCogs, FaLayerGroup,
  FaChevronRight, FaFileAlt, FaBuilding
} from 'react-icons/fa';
import * as pdfjsLib from 'pdfjs-dist';
import { getApiEndpoint } from '../../../config/api';
import AssetDetailModal from '../../compliance/RoofSafety/shared/AssetDetailModal';
import './DrawingHotspots.css';

/* ── Asset table (plural) → singular asset type for history modal ── */
const TABLE_TO_ASSET_TYPE = {
  anchor_points: 'anchor_point',
  cradles: 'cradle',
  davit_bases: 'abseil_system',
  ladders: 'ladder',
  handrails: 'handrail',
  static_lines: 'static_line',
  walkways: 'walkway',
  fixed_equipment: 'fixed_equipment',
};

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

/* ── Asset table → category ──────────────────────────────────── */
const ROOF_TABLES = ['anchor_points', 'cradles', 'davit_bases', 'ladders', 'handrails', 'static_lines', 'walkways'];
const FIRE_TABLES = ['fire_alarms', 'sprinklers', 'emergency_lights', 'fire_doors', 'fire_extinguishers', 'smoke_detectors', 'heat_detectors'];
const EQUIP_TABLES = ['fixed_equipment'];

function getCategory(assetTable) {
  if (ROOF_TABLES.includes(assetTable)) return 'roof';
  if (FIRE_TABLES.includes(assetTable)) return 'fire';
  if (EQUIP_TABLES.includes(assetTable)) return 'equipment';
  return 'default';
}
function getCategoryIcon(cat) {
  switch (cat) {
    case 'roof': return FaHardHat;
    case 'fire': return FaFire;
    case 'equipment': return FaCogs;
    default: return FaMapPin;
  }
}
function formatTableName(t) {
  return (t || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const PIN_COLORS = { roof: '#1A80E5', fire: '#ef4444', equipment: '#d97706', default: '#6b7280' };

function PinSvg({ color = 'currentColor' }) {
  return (
    <svg viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 22 14 22s14-11.5 14-22C28 6.268 21.732 0 14 0z" fill={color} />
      <circle cx="14" cy="13" r="6" fill="#fff" opacity="0.9" />
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════════
   DrawingHotspots — gallery + viewer
   ════════════════════════════════════════════════════════════════ */
export default function DrawingHotspots() {
  const ctx = useOutletContext();
  const { property, id: propertyId } = ctx;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem('token');

  /* ── View state: 'gallery' or 'viewer' ─────────────────────── */
  const [view, setView] = useState('gallery');

  /* ── Shared data ────────────────────────────────────────────── */
  const [drawings, setDrawings] = useState([]);
  const [allHotspots, setAllHotspots] = useState([]);  // hotspot counts per drawing
  const [loading, setLoading] = useState(true);
  const [thumbs, setThumbs] = useState({}); // drawing.id → data URL preview

  /* ── Gallery state ──────────────────────────────────────────── */
  const [activeFloor, setActiveFloor] = useState('all');

  /* ── Viewer state ───────────────────────────────────────────── */
  const [activeDrawingId, setActiveDrawingId] = useState(null);
  const [hotspots, setHotspots] = useState([]);
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [historyHotspot, setHistoryHotspot] = useState(null);
  const [drawingBlobUrl, setDrawingBlobUrl] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);
  const imgRef = useRef(null);
  const viewerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const isPinchingRef = useRef(false);
  const lastPinchDistRef = useRef(null);
  const lastPinchMidRef = useRef(null);
  const isDraggingRef = useRef(false);
  const lastMouseRef = useRef(null);

  // Reset zoom/pan when switching drawings
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  /* ── Fetch drawings + all hotspot counts ───────────────────── */
  const fetchDrawings = useCallback(async () => {
    try {
      const res = await fetch(getApiEndpoint(`/api/properties/${propertyId}/drawings`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDrawings(data);
      }
    } catch (err) { console.error('Error fetching drawings:', err); }
  }, [propertyId, token]);

  const fetchAllHotspots = useCallback(async () => {
    try {
      const res = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/drawings/all-hotspots`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) setAllHotspots(await res.json());
    } catch { /* non-fatal */ }
  }, [propertyId, token]);

  useEffect(() => {
    Promise.all([fetchDrawings(), fetchAllHotspots()]).finally(() => setLoading(false));
  }, [fetchDrawings, fetchAllHotspots]);

  // Auto-open a drawing when navigated from Documents page (?open=documentId)
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId && drawings.length) {
      const target = drawings.find(d => String(d.id) === String(openId));
      if (target) openViewer(target.id);
    }
  }, [searchParams, drawings]); // eslint-disable-line

  /* ── Generate thumbnail previews per drawing ────────────────── */
  useEffect(() => {
    if (!drawings.length) return;
    let cancelled = false;
    const generated = []; // object URLs we create for raster images, for cleanup

    (async () => {
      for (const drawing of drawings) {
        if (cancelled) break;
        if (thumbs[drawing.id]) continue; // already done
        const isPdf = (drawing.mime_type || '').includes('pdf') ||
                      (drawing.original_filename || '').toLowerCase().endsWith('.pdf');
        try {
          const res = await fetch(
            getApiEndpoint(`/api/properties/${propertyId}/documents/${drawing.id}/download`),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!res.ok) continue;
          if (isPdf) {
            const buf = await res.arrayBuffer();
            if (cancelled) break;
            const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(buf), isEvalSupported: false }).promise;
            const page = await pdf.getPage(1);
            // Render at a size that looks crisp on a ~300px card without hurting memory.
            const baseViewport = page.getViewport({ scale: 1 });
            const targetWidth = 480;
            const scale = targetWidth / baseViewport.width;
            const viewport = page.getViewport({ scale });
            const canvas = document.createElement('canvas');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
            if (cancelled) break;
            const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
            setThumbs(prev => ({ ...prev, [drawing.id]: dataUrl }));
          } else {
            const blob = await res.blob();
            if (cancelled) break;
            const url = URL.createObjectURL(blob);
            generated.push(url);
            setThumbs(prev => ({ ...prev, [drawing.id]: url }));
          }
        } catch { /* skip this drawing's thumb */ }
      }
    })();

    return () => {
      cancelled = true;
      generated.forEach(u => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawings, propertyId, token]);

  /* ── Fetch hotspots for active drawing (viewer) ─────────────── */
  const fetchHotspots = useCallback(async () => {
    if (!activeDrawingId) return;
    try {
      const res = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/drawings/${activeDrawingId}/hotspots`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) setHotspots(await res.json());
    } catch (err) { console.error('Error fetching hotspots:', err); }
  }, [propertyId, activeDrawingId, token]);

  useEffect(() => { fetchHotspots(); }, [fetchHotspots]);

  /* ── Load drawing blob (viewer) ─────────────────────────────── */
  const activeDrawing = drawings.find(d => d.id === activeDrawingId);

  useEffect(() => {
    if (!activeDrawing) { setDrawingBlobUrl(null); return; }
    let cancelled = false;
    setImgLoading(true);
    setDrawingBlobUrl(null);

    const isPdf = (activeDrawing.mime_type || '').includes('pdf') ||
                  (activeDrawing.original_filename || '').toLowerCase().endsWith('.pdf');

    fetch(getApiEndpoint(`/api/properties/${propertyId}/documents/${activeDrawing.id}/download`), {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => { if (!r.ok) throw new Error('Download failed'); return isPdf ? r.arrayBuffer() : r.blob(); })
      .then(async (data) => {
        if (cancelled) return;
        if (isPdf) {
          const pdf = await pdfjsLib.getDocument({ data, isEvalSupported: false }).promise;
          const page = await pdf.getPage(1);
          const scale = 2;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const pdfCtx = canvas.getContext('2d');
          await page.render({ canvasContext: pdfCtx, viewport }).promise;
          canvas.toBlob((blob) => {
            if (cancelled) return;
            setDrawingBlobUrl(URL.createObjectURL(blob));
            setImgLoading(false);
          }, 'image/png');
        } else {
          setDrawingBlobUrl(URL.createObjectURL(data));
          setImgLoading(false);
        }
      })
      .catch(() => setImgLoading(false));

    return () => {
      cancelled = true;
      setDrawingBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [activeDrawing?.id, propertyId, token]);

  /* ── Delete a drawing ──────────────────────────────────────── */
  const deleteDrawing = async (drawing, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const name = drawing.name || drawing.original_filename || `Drawing #${drawing.id}`;
    if (!window.confirm(`Delete "${name}"? This removes the drawing and any hotspots on it. This cannot be undone.`)) return;
    try {
      const res = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/documents/${drawing.id}`),
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || body.message || `Delete failed (${res.status})`);
      }
      setDrawings(prev => prev.filter(d => d.id !== drawing.id));
      setAllHotspots(prev => (Array.isArray(prev) ? prev.filter(h => h.document_id !== drawing.id) : prev));
      setThumbs(prev => {
        const next = { ...prev };
        const old = next[drawing.id];
        if (old && typeof old === 'string' && old.startsWith('blob:')) URL.revokeObjectURL(old);
        delete next[drawing.id];
        return next;
      });
    } catch (err) {
      window.alert(err.message || 'Failed to delete drawing');
    }
  };

  /* ── Open a drawing in the viewer ──────────────────────────── */
  const openViewer = (drawingId) => {
    setActiveDrawingId(drawingId);
    setSelectedHotspot(null);
    setHotspots([]);
    setView('viewer');
    resetView();
  };

  const closeViewer = () => {
    setView('gallery');
    setActiveDrawingId(null);
    setSelectedHotspot(null);
    setHotspots([]);
    resetView();
  };

  /* ── Navigate to asset (viewer) ─────────────────────────────── */
  const TABLE_TO_SUBSECTION = {
    anchor_points: 'anchorPoints', cradles: 'cradles', davit_bases: 'abseilSystems',
    ladders: 'ladders', handrails: 'handrails', static_lines: 'staticLines', walkways: 'walkways',
    fire_alarms: 'fireAlarms', sprinklers: 'sprinklers', emergency_lights: 'emergencyLights',
    fire_doors: 'fireDoors', fire_extinguishers: 'fireExtinguishers',
    smoke_detectors: 'smokeDetectors', heat_detectors: 'heatDetectors',
    fixed_equipment: 'fixedEquipment'
  };

  const navigateToAsset = (hotspot) => {
    const table = hotspot.asset_table;
    const base = `/properties/${propertyId}`;
    const subSection = TABLE_TO_SUBSECTION[table] || '';
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('propertySearchNavigate', { detail: { subSection } }));
      window.dispatchEvent(new CustomEvent('openAssetFromHotspot', { detail: { assetId: hotspot.asset_id, assetTable: table } }));
    }, 100);
    if (ROOF_TABLES.includes(table)) navigate(`${base}/roof-safety`);
    else if (FIRE_TABLES.includes(table)) navigate(`${base}/fire-safety`);
    else if (EQUIP_TABLES.includes(table)) navigate(`${base}/equipment-safety`);
    else navigate(base);
  };

  /* ── Escape closes popover ──────────────────────────────────── */
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSelectedHotspot(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /* ── Floor grouping for gallery ─────────────────────────────── */
  const floorLabels = ['all', ...Array.from(
    new Set(drawings.map(d => d.floor_level || d.level || null).filter(Boolean))
  ).sort()];

  const filteredDrawings = activeFloor === 'all'
    ? drawings
    : drawings.filter(d => (d.floor_level || d.level) === activeFloor);

  /* ── Hotspot count per drawing ──────────────────────────────── */
  const countForDrawing = (drawingId) => {
    if (Array.isArray(allHotspots)) {
      return allHotspots.filter(h => h.document_id === drawingId).length;
    }
    return 0;
  };

  /* ── Loading ─────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="dh-page">
        <div className="dh-empty"><p>Loading drawings…</p></div>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════════
     GALLERY VIEW
     ════════════════════════════════════════════════════════════ */
  if (view === 'gallery') {
    return (
      <div className="dh-page">
        {/* Header */}
        <div className="dh-gallery-header">
          <div className="dh-gallery-header-icon"><FaFileAlt /></div>
          <div>
            <h1 className="dh-gallery-title">Drawings</h1>
            <p className="dh-gallery-subtitle">
              {property?.name || 'Property'} · {drawings.length} drawing{drawings.length !== 1 ? 's' : ''} · Click to view interactive hotspots
            </p>
          </div>
        </div>

        {drawings.length === 0 ? (
          <div className="dh-empty">
            <FaBuilding className="dh-empty-ico" />
            <h3>No Drawings Available</h3>
            <p>No drawings have been added for this property yet. Contact your CloudSafe team to have floor plans uploaded.</p>
          </div>
        ) : (
          <div className="dh-gallery-layout">
            {/* Floor sidebar */}
            <aside className="dh-floor-sidebar">
              <div className="dh-floor-sidebar-label">Filter by level</div>
              {floorLabels.map(floor => (
                <button
                  key={floor || 'all'}
                  className={`dh-floor-btn ${activeFloor === floor ? 'active' : ''}`}
                  onClick={() => setActiveFloor(floor)}
                >
                  <span className="dh-floor-name">{floor === 'all' ? 'All Drawings' : floor}</span>
                  <span className="dh-floor-count">
                    {floor === 'all'
                      ? drawings.length
                      : drawings.filter(d => (d.floor_level || d.level) === floor).length}
                  </span>
                  {activeFloor === floor && <FaChevronRight className="dh-floor-chevron" />}
                </button>
              ))}
            </aside>

            {/* Drawing cards */}
            <div className="dh-gallery-content">
              <div className="dh-gallery-content-header">
                <h2 className="dh-gallery-content-title">
                  {activeFloor === 'all' ? 'All Drawings' : activeFloor}
                </h2>
                <span className="dh-gallery-content-count">
                  {filteredDrawings.length} drawing{filteredDrawings.length !== 1 ? 's' : ''}
                </span>
              </div>

              {filteredDrawings.length === 0 ? (
                <div className="dh-empty" style={{ padding: '40px 24px' }}>
                  <FaImage className="dh-empty-ico" />
                  <p>No drawings for this level.</p>
                </div>
              ) : (
                <div className="dh-drawing-grid">
                  {filteredDrawings.map(drawing => {
                    const count = countForDrawing(drawing.id);
                    const isPdf = (drawing.mime_type || '').includes('pdf') ||
                                  (drawing.original_filename || '').toLowerCase().endsWith('.pdf');
                    return (
                      <button
                        key={drawing.id}
                        className="dh-drawing-card"
                        onClick={() => openViewer(drawing.id)}
                      >
                        <div className="dh-drawing-card-thumb">
                          {thumbs[drawing.id] ? (
                            <img
                              src={thumbs[drawing.id]}
                              alt={drawing.name || drawing.original_filename || `Drawing #${drawing.id}`}
                              loading="lazy"
                            />
                          ) : isPdf ? (
                            <div className="dh-drawing-card-pdf-placeholder">
                              <FaFileAlt />
                              <span>Rendering preview…</span>
                            </div>
                          ) : (
                            <div className="dh-drawing-card-img-placeholder">
                              <FaImage />
                            </div>
                          )}
                          {isPdf && thumbs[drawing.id] && (
                            <span className="dh-drawing-card-badge">PDF</span>
                          )}
                          <button
                            type="button"
                            className="dh-drawing-card-delete"
                            onClick={(e) => deleteDrawing(drawing, e)}
                            title="Delete drawing"
                            aria-label="Delete drawing"
                          >
                            <FaTrash />
                          </button>
                          <div className="dh-drawing-card-overlay">
                            <span className="dh-drawing-card-overlay-btn">
                              <FaExternalLinkAlt /> Open Drawing
                            </span>
                          </div>
                        </div>
                        <div className="dh-drawing-card-body">
                          <div className="dh-drawing-card-info">
                            <h3 className="dh-drawing-card-name">
                              {drawing.name || drawing.original_filename || `Drawing #${drawing.id}`}
                            </h3>
                            {(drawing.floor_level || drawing.level) && (
                              <span className="dh-drawing-card-level">
                                {drawing.floor_level || drawing.level}
                              </span>
                            )}
                          </div>
                          <div className="dh-drawing-card-meta">
                            <span className="dh-drawing-card-hotspots">
                              <FaMapPin /> {count} hotspot{count !== 1 ? 's' : ''}
                            </span>
                            <FaChevronRight className="dh-drawing-card-arrow" />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════════
     VIEWER VIEW
     ════════════════════════════════════════════════════════════ */
  return (
    <div className="dh-page">
      {/* Top bar */}
      <div className="dh-topbar">
        <button className="dh-back-btn" onClick={closeViewer}>
          <FaArrowLeft /> Back to Drawings
        </button>
        <div className="dh-topbar-title">
          <h1>{activeDrawing?.name || activeDrawing?.original_filename || 'Drawing'}</h1>
          <p>{property?.name || 'Building'} · {hotspots.filter(h => h.document_id === activeDrawingId).length} hotspot{hotspots.filter(h => h.document_id === activeDrawingId).length !== 1 ? 's' : ''} mapped</p>
        </div>
        <div className="dh-topbar-actions">
          <div className="dh-zoom-controls">
            <button className="dh-zoom-btn" onClick={() => setZoom(z => Math.min(8, z * 1.25))} title="Zoom in">+</button>
            <button className="dh-zoom-btn" onClick={resetView} title="Reset">{Math.round(zoom * 100)}%</button>
            <button className="dh-zoom-btn" onClick={() => setZoom(z => Math.max(0.5, z * 0.8))} title="Zoom out">−</button>
          </div>
        </div>
      </div>

      {/* Canvas + side panel */}
      <div
        className="dh-canvas-wrap"
        ref={viewerRef}
        style={{ overflow: 'hidden', cursor: isDraggingRef.current ? 'grabbing' : 'grab' }}
        onWheel={e => {
          e.preventDefault();
          const delta = e.deltaY < 0 ? 1.1 : 0.9;
          setZoom(z => Math.min(8, Math.max(0.5, z * delta)));
        }}
        onMouseDown={e => { isDraggingRef.current = true; lastMouseRef.current = { x: e.clientX, y: e.clientY }; }}
        onMouseMove={e => {
          if (!isDraggingRef.current || !lastMouseRef.current) return;
          const dx = e.clientX - lastMouseRef.current.x;
          const dy = e.clientY - lastMouseRef.current.y;
          setPan(p => ({ x: p.x + dx, y: p.y + dy }));
          lastMouseRef.current = { x: e.clientX, y: e.clientY };
        }}
        onMouseUp={() => { isDraggingRef.current = false; lastMouseRef.current = null; }}
        onMouseLeave={() => { isDraggingRef.current = false; lastMouseRef.current = null; }}
        onTouchStart={e => {
          if (e.touches.length === 2) {
            isPinchingRef.current = true;
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            lastPinchDistRef.current = Math.hypot(dx, dy);
            lastPinchMidRef.current = {
              x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
              y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
            };
          }
        }}
        onTouchMove={e => {
          if (e.touches.length === 2 && isPinchingRef.current) {
            e.preventDefault();
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const dist = Math.hypot(dx, dy);
            if (lastPinchDistRef.current) {
              const ratio = dist / lastPinchDistRef.current;
              setZoom(z => Math.min(8, Math.max(0.5, z * ratio)));
            }
            lastPinchDistRef.current = dist;
          }
        }}
        onTouchEnd={e => { if (e.touches.length < 2) { isPinchingRef.current = false; lastPinchDistRef.current = null; } }}
      >
        <div className="dh-canvas">
          <div className="dh-canvas-inner" style={{ transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`, transformOrigin: '0 0', willChange: 'transform' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              {imgLoading && <p style={{ color: '#9ca3af', padding: 40 }}>Loading drawing…</p>}
              {drawingBlobUrl && (
                <img
                  ref={imgRef}
                  src={drawingBlobUrl}
                  alt={activeDrawing?.name}
                  className="dh-canvas-img"
                  draggable={false}
                />
              )}

              {/* Hotspot pins */}
              {hotspots.filter(h => h.document_id === activeDrawingId).map(h => {
                const cat = getCategory(h.asset_table);
                const pinColor = PIN_COLORS[cat];
                return (
                  <div
                    key={h.id}
                    className={`dh-hotspot dh-pin-${cat} ${selectedHotspot?.id === h.id ? 'selected' : ''}`}
                    style={{ left: `${h.x_percent}%`, top: `${h.y_percent}%` }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setHistoryHotspot(h);
                    }}
                  >
                    <div className="dh-hotspot-pin"><PinSvg color={pinColor} /></div>
                    <span className="dh-hotspot-label">{h.label || 'Untitled'}</span>
                  </div>
                );
              })}

              {/* Desktop popover */}
              {selectedHotspot && (() => {
                const h = selectedHotspot;
                const cat = getCategory(h.asset_table);
                const CatIcon = getCategoryIcon(cat);
                const popLeft = h.x_percent > 60 ? `${h.x_percent - 2}%` : `${h.x_percent + 2}%`;
                const popTop = `${Math.max(5, h.y_percent - 5)}%`;
                const popStyle = {
                  left: popLeft, top: popTop,
                  transform: h.x_percent > 60 ? 'translateX(-100%)' : 'none'
                };
                return (
                  <div className="dh-popover dh-popover-desktop" style={popStyle} onClick={e => e.stopPropagation()}>
                    <div className="dh-popover-header">
                      <div className={`dh-popover-ico dh-item-ico-${cat}`}><CatIcon /></div>
                      <div className="dh-popover-info">
                        <div className="dh-popover-name">{h.label || 'Untitled'}</div>
                        <div className="dh-popover-type">{formatTableName(h.asset_table)}</div>
                      </div>
                      <button className="dh-modal-close" onClick={() => setSelectedHotspot(null)}><FaTimes /></button>
                    </div>
                    {h.note && <div className="dh-popover-body"><p className="dh-popover-note">{h.note}</p></div>}
                    <div className="dh-popover-actions">
                      <button className="dh-popover-action dh-popover-action-view" onClick={() => navigateToAsset(h)}>
                        <FaExternalLinkAlt /> View Asset
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Side panel — hotspot list */}
        <div className="dh-side-panel">
          <div className="dh-panel-header">
            <h3><FaLayerGroup /> Hotspots</h3>
            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', background: '#f3f4f6', padding: '2px 10px', borderRadius: 20 }}>
              {hotspots.filter(h => h.document_id === activeDrawingId).length}
            </span>
          </div>
          <div className="dh-panel-body">
            {hotspots.filter(h => h.document_id === activeDrawingId).length === 0 ? (
              <div className="dh-empty" style={{ padding: '30px 16px' }}>
                <FaMapPin style={{ fontSize: '1.5rem', color: '#d1d5db' }} />
                <p style={{ fontSize: '0.78rem' }}>No asset hotspots have been mapped on this drawing yet.</p>
              </div>
            ) : (
              <div className="dh-hotspot-list">
                {hotspots.filter(h => h.document_id === activeDrawingId).map(h => {
                  const cat = getCategory(h.asset_table);
                  const CatIcon = getCategoryIcon(cat);
                  return (
                    <button
                      key={h.id}
                      className={`dh-hotspot-item ${historyHotspot?.id === h.id ? 'active' : ''}`}
                      onClick={() => setHistoryHotspot(h)}
                    >
                      <div className={`dh-item-ico dh-item-ico-${cat}`}><CatIcon /></div>
                      <div className="dh-item-info">
                        <div className="dh-item-name">{h.label || 'Untitled'}</div>
                        <div className="dh-item-type">{formatTableName(h.asset_table)}</div>
                      </div>
                      <FaChevronRight style={{ fontSize: '0.6rem', color: '#d1d5db', flexShrink: 0 }} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile popover */}
      {selectedHotspot && (() => {
        const h = selectedHotspot;
        const cat = getCategory(h.asset_table);
        const CatIcon = getCategoryIcon(cat);
        return (
          <div className="dh-popover dh-popover-mobile" onClick={e => e.stopPropagation()}>
            <div className="dh-popover-header">
              <div className={`dh-popover-ico dh-item-ico-${cat}`}><CatIcon /></div>
              <div className="dh-popover-info">
                <div className="dh-popover-name">{h.label || 'Untitled'}</div>
                <div className="dh-popover-type">{formatTableName(h.asset_table)}</div>
              </div>
              <button className="dh-modal-close" onClick={() => setSelectedHotspot(null)}><FaTimes /></button>
            </div>
            {h.note && <div className="dh-popover-body"><p className="dh-popover-note">{h.note}</p></div>}
            <div className="dh-popover-actions">
              <button className="dh-popover-action dh-popover-action-view" onClick={() => navigateToAsset(h)}>
                <FaExternalLinkAlt /> View Asset
              </button>
            </div>
          </div>
        );
      })()}

      {/* Asset history modal — opens when a pin or sidebar item is clicked */}
      <AssetDetailModal
        isOpen={!!historyHotspot}
        onClose={() => setHistoryHotspot(null)}
        asset={historyHotspot ? {
          id: historyHotspot.asset_id,
          name: historyHotspot.label || 'Untitled',
          location: activeDrawing?.name || activeDrawing?.original_filename || '',
        } : null}
        assetType={historyHotspot ? TABLE_TO_ASSET_TYPE[historyHotspot.asset_table] : null}
        propertyId={propertyId}
      />
    </div>
  );
}
